import { Link, useLocation } from 'react-router-dom';
import ChevronLeftIcon from '@/assets/icons/chevron-left.svg?react';
import BarsIcon from '@/assets/icons/bars.svg?react';
import XmarkIcon from '@/assets/icons/xmark.svg?react';
import UsersIcon from '@/assets/icons/users.svg?react';
import DatabaseIcon from '@/assets/icons/newspaper.svg?react';
import DataflowIcon from '@/assets/icons/dataflow.svg?react';

import styles from './navbar.module.scss';
import { useEffect, useState } from 'react';
import { classnames, truncateText } from '@/utils/functions';
import UserOptions from './UserOptions';
import useResponsive from '@/hooks/useResponsive';
import Button from '../Button';
import { Role } from '@/models/user';
import List from '../List';
import useSession from '@/hooks/useSession';

const NAVBAR_STORE_KEY = 'navbarCollapsed';

const Navbar = () => {
  const getCollapsedValueFromStore = () =>
    localStorage.getItem(NAVBAR_STORE_KEY) === 'true';

  const [collapsed, setCollapsed] = useState(getCollapsedValueFromStore());
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const { session, pushUserEvent, orgIndex } = useSession();
  const { threads, organizations } = session;

  const toggleMobileMenu = () => {
    setShowMobileMenu((current) => !current);
  };
  const toggleCollapse = () => {
    setCollapsed((current) => {
      const newValue = !current;
      localStorage.setItem(NAVBAR_STORE_KEY, newValue ? 'true' : 'false');

      pushUserEvent(`${newValue ? 'collapse' : 'expand'}_navbar`);
      return newValue;
    });
  };

  useResponsive(
    () => {
      setCollapsed(false);
      setShowMobileMenu(false);
    },
    () => {
      setCollapsed(getCollapsedValueFromStore());
      setShowMobileMenu(false);
    }
  );

  const location = useLocation();

  useEffect(() => {
    setShowMobileMenu(false);
  }, [location]);

  if (session == null) {
    return null;
  }

  return (
    <nav
      className={classnames(
        styles['navbar'],
        { [styles['collapsed']]: collapsed },
        { [styles['show-mobile-content']]: showMobileMenu }
      )}
    >
      <button
        data-testid="navbar-trigger"
        className={styles['navbar-trigger']}
        onClick={toggleMobileMenu}
      >
        {showMobileMenu ? <XmarkIcon /> : <BarsIcon />}
      </button>
      <Link
        className={styles['logo-wrapper']}
        to="/"
        title={
          organizations[orgIndex].name
            ? `GANNET Workspace for ${organizations[orgIndex].name}`
            : 'GANNET Workspace'
        }
      >
        <img src="/gannet-white.png" height="40" alt="Gannet white logo" />

        {organizations[orgIndex].logo ? (
          <>
            <XmarkIcon />
            <img
              className={styles['org-logo']}
              src={organizations[orgIndex].logo}
              alt={`${organizations[orgIndex].name} Logo`}
            />
          </>
        ) : organizations[orgIndex].name ? (
          <span
            className={styles['org-name']}
            title={organizations[orgIndex].name}
          >
            {truncateText(organizations[orgIndex].name, 30)}
          </span>
        ) : null}
      </Link>
      <div className={styles['navbar-content']}>
        <div className={styles['navbar-menu']}>
          <Link data-testid={'new-thread-btn'} to="/">
            <Button
              className={styles['new-thread-btn']}
              onClick={() => {
                pushUserEvent('click_new_thread');
              }}
            >
              {collapsed ? '+' : 'New Query'}
            </Button>
          </Link>
          <Link to="/library">
            <Button variation="secondary" className={styles['icon-btn']}>
              {collapsed ? <DatabaseIcon /> : `Library (${session.fileCount})`}
            </Button>
          </Link>
          {session.roles.some((role) =>
            [Role.ORG_USER_ADMIN, Role.SUPER_USER_ADMIN].includes(role)
          ) && (
            <Link to="/users-management">
              <Button variation="tertiary">
                {collapsed ? <UsersIcon /> : 'Users'}
              </Button>
            </Link>
          )}
          {session.roles.some((role) =>
            [Role.SUPER_ORG_ADMIN].includes(role)
          ) && (
            <Link to="/orgs-management">
              <Button variation="tertiary">
                {collapsed ? <DataflowIcon /> : 'Organizations'}
              </Button>
            </Link>
          )}
        </div>
        {!collapsed && (
          <>
            <span className={styles['heading']}>
              Threads ({threads.length})
              <Link
                to="search"
                onClick={() => {
                  pushUserEvent('click_search');
                }}
              >
                Search
              </Link>
            </span>
            <List
              items={threads.map(({ id, title, items }) => ({
                path: `/thread/${id}`,
                title: title,
                subtitle: items[items.length - 1].queryDate.toLocaleString(),
              }))}
            />
          </>
        )}
        <UserOptions collapsed={collapsed} />

        <button className={styles['collapse-trigger']} onClick={toggleCollapse}>
          <ChevronLeftIcon />
        </button>
      </div>
    </nav>
  );
};

export default Navbar;
